/**
 * Copyright Open Logistics Foundation
 *
 * Licensed under the Open Logistics Foundation License 1.3.
 * For details on the licensing terms, see the LICENSE file.
 * SPDX-License-Identifier: OLFL-1.3
 */

import { Injectable } from '@angular/core';
import {
    HttpBackend,
    HttpClient,
    HttpEvent,
    HttpResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { EcmrDocument } from '@app/shared/models/ecmr/EcmrDocument';
import { EcmrUrlResolverService } from './ecmr-url-resolver.service';

@Injectable({
    providedIn: 'root',
})
export class EcmrDocumentService {
    private anonymousHttp: HttpClient;

    constructor(
        private http: HttpClient,
        handler: HttpBackend,
        private ecmrUrlResolver: EcmrUrlResolverService
    ) {
        this.anonymousHttp = new HttpClient(handler);
    }

    uploadDocument(
        ecmrId: string,
        file: File,
        userToken?: string | null,
        tan?: string | null
    ): Observable<HttpEvent<any>> {
        const formData = new FormData();
        formData.append('file', file);

        if (userToken && tan) {
            return this.anonymousHttp.post(
                `${this.ecmrUrlResolver.getEcmrUrl()}/anonymous/document`,
                formData,
                {
                    params: { ecmrId, userToken, tan },
                    reportProgress: true,
                    observe: 'events',
                }
            );
        }

        return this.http.post(
            `${this.ecmrUrlResolver.getEcmrUrl()}/document`,
            formData,
            {
                params: { ecmrId },
                reportProgress: true,
                observe: 'events',
            }
        );
    }

    listDocuments(
        ecmrId: string,
        userToken?: string | null,
        tan?: string | null
    ): Observable<EcmrDocument[]> {
        if (userToken && tan) {
            return this.anonymousHttp.get<EcmrDocument[]>(
                `${this.ecmrUrlResolver.getEcmrUrl()}/anonymous/document`,
                { params: { ecmrId, userToken, tan } }
            );
        }

        return this.http.get<EcmrDocument[]>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/document`,
            { params: { ecmrId } }
        );
    }

    downloadDocument(
        id: number,
        userToken?: string | null,
        tan?: string | null
    ): Observable<HttpResponse<Blob>> {
        if (userToken && tan) {
            return this.anonymousHttp.get(
                `${this.ecmrUrlResolver.getEcmrUrl()}/anonymous/document/${id}/download`,
                {
                    params: { userToken, tan },
                    responseType: 'blob',
                    observe: 'response',
                }
            );
        }

        return this.http.get(
            `${this.ecmrUrlResolver.getEcmrUrl()}/document/${id}/download`,
            {
                responseType: 'blob',
                observe: 'response',
            }
        );
    }

    deleteDocument(
        id: number,
        userToken?: string | null,
        tan?: string | null
    ): Observable<void> {
        if (userToken && tan) {
            return this.anonymousHttp.delete<void>(
                `${this.ecmrUrlResolver.getEcmrUrl()}/anonymous/document/${id}`,
                { params: { userToken, tan } }
            );
        }

        return this.http.delete<void>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/document/${id}`
        );
    }
}
